import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './add-tax-identifiers.module.css';
import { BulkActions } from '../../../../enums/bulk-actions';
import { TaxIdTypeMessages } from '../../../../enums/tax-identifiers/tax-identifiers-types';
import { useTaxIdentifiers } from '../../../../hooks/use-tax-identifier';
import { groupByTaxIdType } from '../../../../services/tax-identifier-service';
import { MultiCheckDropdown } from '../../../multi-check-dropdown';
import { PillBank } from '../../../pill-bank';

const messages = defineMessages({
  infoMessage: {
    description: 'bulkActionsAddTaxIdentifiers.description',
    defaultMessage: "Select from your configured tax identifiers to apply to your orders' customs declarations",
    id: 'nE34rS',
  },
  overwriteMessage: {
    description: 'bulkActionsAddTaxIdentifiers.overwriteMessage',
    defaultMessage: 'If the selected shipments have exisiting tax ids, this will overwrite them',
    id: 'tYujjs',
  },
  toggleLabel: {
    description: 'bulkActionsAddTaxIdentifiers.toggleLabel',
    defaultMessage: 'Select Tax Identifiers',
    id: 'PRiZV2',
  },
  loadingLabel: {
    description: 'Loading bulk add tax identifiers',
    defaultMessage: 'Loading...',
    id: 'fvuXuN',
  },
  noSellerTaxIdentifiers: {
    description: 'No tax identifiers set go to setting CTA',
    defaultMessage: 'Currently there are no Tax Identifiers configured in this account',
    id: '7phLTr',
  },
  sellerTaxIdentifiersInstructions: {
    description: 'Instructions for how to set up Tax Identifiers',
    defaultMessage: 'To update orders with Tax Identifiers, add and configure Tax Identifiers on the <a>International Settings Page</a>.',
    id: 'EAimIE',
  },
});

const AddTaxIdentifiersComponent = ({ onChange, model, onClose }) => {
  const intl = useIntl();
  const { taxIdentifiers } = useTaxIdentifiers({ includeInactive: false });
  const [selectedTaxIdentifierIds, setSelectedTaxIdentifierIds] = useAsyncSafeState([]);

  useEffect(() => {
    onChange({
      ...model,
      taxIds: selectedTaxIdentifierIds,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaxIdentifierIds]);

  const sellerTaxIdentifierDisplayMap = useMemo(() => {
    return Object.fromEntries(taxIdentifiers.map((value) => [value.taxIdentifierId, `${value.name} (${value.redactedTaxNumber})`]));
  }, [taxIdentifiers]);
  const selectableTaxIdGroups = useMemo(() => {
    return groupByTaxIdType(taxIdentifiers);
  }, [taxIdentifiers]);

  const onUpdateTaxIds = (taxIdentifierIds) => {
    setSelectedTaxIdentifierIds(taxIdentifierIds);
  };

  const renderTaxIdLabel = (itemId) => {
    return sellerTaxIdentifierDisplayMap[itemId];
  };

  const getTaxIdTypeLabel = (taxIdType) => {
    const messageDescriptor = TaxIdTypeMessages[taxIdType];
    if (!messageDescriptor) {
      return taxIdType;
    }
    return intl.formatMessage(messageDescriptor);
  };

  AddTaxIdentifiersComponent.propTypes = {
    onChange: PropTypes.func.isRequired,
    model: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  const toggleLabel = <FormattedMessage {...messages.toggleLabel} />;
  return taxIdentifiers.length > 0 ? (
    <>
      <div className={style['info-message']}>
        <FormattedMessage {...messages.infoMessage} />
      </div>
      <MultiCheckDropdown
        dropdownMenuShouldHaveWidthOfButton
        isManualApply
        items={selectableTaxIdGroups}
        onUpdate={onUpdateTaxIds}
        renderItemLabel={renderTaxIdLabel}
        renderSectionLabel={getTaxIdTypeLabel}
        selectedItems={selectedTaxIdentifierIds}
        shouldUpdateOnClose
        toggleLabel={toggleLabel}
      />
      <PillBank labelFormatter={renderTaxIdLabel} onUpdate={onUpdateTaxIds} selectedItems={selectedTaxIdentifierIds} />
      <div className={style['overwrite-message']}>
        <FormattedMessage {...messages.overwriteMessage} />
      </div>
    </>
  ) : (
    <div className={style['no-ids-container']}>
      <div className={style['no-ids-message']}>
        <FormattedMessage {...messages.noSellerTaxIdentifiers} />
      </div>
      <div className={style['no-ids-instructions']}>
        <FormattedMessage
          {...messages.sellerTaxIdentifiersInstructions}
          values={{
            a: (chunk) => (
              <NavLink onClick={onClose} to='/settings/international'>
                {chunk}
              </NavLink>
            ),
          }}
        />
      </div>
    </div>
  );
};

export const AddTaxIdentifiers = {
  id: BulkActions.addTaxIdentifiers.name,

  render: (onChange, model, jobData, isEmpty, onClose) => {
    return <AddTaxIdentifiersComponent model={model} onChange={onChange} onClose={onClose} />;
  },
};
